import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n, { setLanguage } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Leaf, Lock } from "lucide-react";

export default function LoginPage() {
  const { login, user, ready } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (ready && user) nav("/", { replace: true }); }, [ready, user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const r = await login(email.trim(), password);
    setLoading(false);
    if (!r.ok) setError(r.error);
    else nav("/", { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 bg-cover bg-center lg:block"
        style={{ backgroundImage: "linear-gradient(rgba(27,67,50,0.55),rgba(27,67,50,0.75)), url('https://images.unsplash.com/photo-1759141936083-d10203b4d4f6?crop=entropy&cs=srgb&fm=jpg&q=85')" }}>
        <div className="flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2 font-serif text-2xl font-semibold"><Leaf className="h-6 w-6" /> Ayurtoday</div>
          <div>
            <h1 className="font-serif text-4xl font-bold leading-tight">{t("90-Day Dinacharya & Gut Health Programme")}</h1>
            <p className="mt-4 max-w-md text-emerald-50/90">{t("Confidential participant assessment portal. Your health information is protected and auto-saved as you go.")}</p>
          </div>
          <p className="text-xs text-emerald-100/70">{t("Corporate Wellness Programme · Confidential")}</p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-[#FAF7F2] px-6 lg:w-1/2">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-5 flex justify-end">
            <div className="flex overflow-hidden rounded-md border border-[#E5DFD3] text-xs" data-testid="lang-toggle">
              {["en", "ta"].map((lng) => (
                <button type="button" key={lng} data-testid={`lang-${lng}`} onClick={() => setLanguage(lng)}
                  className={`px-2.5 py-1 font-medium transition-colors ${i18n.language === lng ? "bg-emerald-800 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}>
                  {lng === "en" ? "EN" : "தமிழ்"}
                </button>
              ))}
            </div>
          </div>
          <div className="mb-6 flex items-center gap-2 text-emerald-900 lg:hidden">
            <Leaf className="h-6 w-6" /><span className="font-serif text-2xl font-semibold">Ayurtoday</span>
          </div>
          <h2 className="font-serif text-3xl font-bold text-stone-900">{t("Welcome back")}</h2>
          <p className="mt-1 mb-6 text-sm text-stone-500">{t("Sign in to your assessment account.")}</p>

          {error && <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{error}</div>}

          <label className="mb-1 block text-sm font-medium text-stone-700">{t("Email")}</label>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="mb-4 w-full rounded-md border border-[#E5DFD3] bg-white px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600/40" />

          <label className="mb-1 block text-sm font-medium text-stone-700">{t("Password")}</label>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            className="mb-6 w-full rounded-md border border-[#E5DFD3] bg-white px-3 py-2.5 text-sm focus:border-emerald-600 focus:outline-none focus:ring-1 focus:ring-emerald-600/40" />

          <button type="submit" disabled={loading} data-testid="login-submit"
            className="flex w-full items-center justify-center gap-2 rounded-md bg-emerald-800 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-900 disabled:opacity-60">
            <Lock className="h-4 w-4" />{loading ? t("Signing in…") : t("Sign in")}
          </button>
          <p className="mt-6 text-center text-xs text-stone-400">{t("Accounts are created by your programme administrator.")}</p>
        </form>
      </div>
    </div>
  );
}
